import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { PremiumStatusProvider } from "@/context/PremiumStatusContext";
import { PremiumSoonProvider } from "@/components/PremiumSoon";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import InstallGuide from "@/pages/InstallGuide";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import TalentDashboard from "@/pages/TalentDashboard";
import TalentPortfolioPage from "@/pages/TalentPortfolioPage";
import TalentProfileEdit from "@/pages/TalentProfileEdit";
import Notifications from "@/pages/Notifications";
import Concours from "@/pages/Concours";
import ConcoursDetail from "@/pages/ConcoursDetail";
import ContestEntryPage from "@/pages/ContestEntryPage";
import MyContestEntries from "@/pages/MyContestEntries";
import PremiumPage from "@/pages/PremiumPage";
import Settings from "@/pages/Settings";
import Collaborations from "@/pages/Collaborations";
import CollaborationDetail from "@/pages/CollaborationDetail";
import MyApplications from "@/pages/MyApplications";
import Events from "@/pages/Events";
import Network from "@/pages/Network";
import Messages from "@/pages/Messages";
import EventDetail from "@/pages/EventDetail";
import MyEvents from "@/pages/MyEvents";
import ProDashboard from "@/pages/ProDashboard";
import ProCollaborationsPage from "@/pages/ProCollaborationsPage";
import ProFavorites from "@/pages/ProFavorites";
import ProProfile from "@/pages/ProProfile";
import ProApplications from "@/pages/ProApplications";
import ProNotifications from "@/pages/ProNotifications";
import ProSettings from "@/pages/ProSettings";
import ProConcours from "@/pages/ProConcours";
import TalentsList from "@/pages/TalentsList";
import TalentProfilePage from "@/pages/TalentProfilePage";
import TalentProfileEditorial from "@/pages/TalentProfileEditorial";
import ProCollabApplications from "@/pages/ProCollabApplications";
import AdminDashboard from "@/pages/AdminDashboard";
import Dashboard from "@/pages/Dashboard";
import FAQ from "@/pages/FAQ";
import CGU from "@/pages/CGU";
import Mentions from "@/pages/Mentions";
import Confidentialite from "@/pages/Confidentialite";
import NotFound from "@/pages/NotFound";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Missions from "@/pages/Missions";
import MissionDetail from "@/pages/MissionDetail";
import TalentUniverse from "@/pages/TalentUniverse";
import CreatorsList from "@/pages/CreatorsList";
import CreatorPublicProfile from "@/pages/CreatorPublicProfile";
import { Toaster } from "sonner";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { useParams } from "react-router-dom";

// Legacy redirect that preserves :id when going from /pro/collaborations/:id/applications
// to the new /createurs/* canonical path.
function LegacyProCollabApplicationsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/createurs/collaborations/${id}/applications`} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle auth callback via URL fragment
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/install" element={<InstallGuide />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/talent" element={<TalentDashboard />} />
      <Route path="/talent/portfolio" element={<TalentPortfolioPage />} />
      <Route path="/talent/profile" element={<TalentProfileEdit />} />
      <Route path="/talent/notifications" element={<Notifications />} />
      <Route path="/talent/concours" element={<Concours />} />
      <Route path="/talent/my-contest-entries" element={<MyContestEntries />} />
      <Route path="/concours/:id" element={<ConcoursDetail />} />
      <Route path="/contest/:cid/entry/:eid" element={<ContestEntryPage />} />
      <Route path="/talent/premium" element={<PremiumPage />} />
      <Route path="/talent/settings" element={<Settings />} />
      <Route path="/talent/applications" element={<MyApplications />} />
      <Route path="/collaborations" element={<Collaborations />} />
      <Route path="/collaborations/:id" element={<CollaborationDetail />} />
      <Route path="/events" element={<Events />} />
      <Route path="/reseau" element={<Network />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/messages/:tid" element={<Messages />} />
      <Route path="/events/:id" element={<EventDetail />} />
      <Route path="/talent/my-events" element={<MyEvents />} />
      {/* ===== Créateurs (new canonical paths) ===== */}
      <Route path="/createurs" element={<ProDashboard />} />
      <Route path="/createurs/collaborations" element={<ProCollaborationsPage />} />
      <Route path="/createurs/talents" element={<TalentsList />} />
      <Route path="/createurs/applications" element={<ProApplications />} />
      <Route path="/createurs/favorites" element={<ProFavorites />} />
      <Route path="/createurs/profile" element={<ProProfile />} />
      <Route path="/createurs/notifications" element={<ProNotifications />} />
      <Route path="/createurs/settings" element={<ProSettings />} />
      <Route path="/createurs/concours" element={<ProConcours />} />
      <Route path="/createurs/missions" element={<Missions />} />
      <Route path="/createurs/collaborations/:id/applications" element={<ProCollabApplications />} />
      {/* ===== Legacy /pro/* → 301-style redirect to /createurs/* ===== */}
      <Route path="/pro" element={<Navigate to="/createurs" replace />} />
      <Route path="/pro/collaborations" element={<Navigate to="/createurs/collaborations" replace />} />
      <Route path="/pro/talents" element={<Navigate to="/createurs/talents" replace />} />
      <Route path="/pro/applications" element={<Navigate to="/createurs/applications" replace />} />
      <Route path="/pro/favorites" element={<Navigate to="/createurs/favorites" replace />} />
      <Route path="/pro/profile" element={<Navigate to="/createurs/profile" replace />} />
      <Route path="/pro/notifications" element={<Navigate to="/createurs/notifications" replace />} />
      <Route path="/pro/settings" element={<Navigate to="/createurs/settings" replace />} />
      <Route path="/pro/concours" element={<Navigate to="/createurs/concours" replace />} />
      <Route path="/pro/missions" element={<Navigate to="/createurs/missions" replace />} />
      <Route path="/pro/collaborations/:id/applications" element={<LegacyProCollabApplicationsRedirect />} />
      {/* ===== Talents public profiles ===== */}
      <Route path="/talents/:id" element={<TalentProfileEditorial />} />
      <Route path="/talents/:id/classic" element={<TalentProfilePage />} />
      <Route path="/admin/*" element={<AdminDashboard />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/mentions-legales" element={<Mentions />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/missions" element={<Missions />} />
      <Route path="/missions/:id" element={<MissionDetail />} />
      <Route path="/talent/missions" element={<Missions />} />
      <Route path="/talent/universe" element={<TalentUniverse />} />
      <Route path="/talent/castings" element={<Events />} />
      <Route path="/talent/createurs" element={<CreatorsList />} />
      <Route path="/createurs/annuaire" element={<CreatorsList />} />
      <Route path="/createurs/:id" element={<CreatorPublicProfile />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <PremiumStatusProvider>
              <PremiumSoonProvider>
                <ErrorBoundary>
                  <AppRouter />
                </ErrorBoundary>
                <Toaster position="top-right" richColors />
                <PWAInstallBanner />
              </PremiumSoonProvider>
            </PremiumStatusProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
